import { useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useDeleteNotification } from "@/hooks/mutations/use-delete-notification";
import { useGetNotification } from "@/hooks/queries/use-get-notification";
import { Breadcrumbs } from "../breadcrumbs";
import { CreateNotificationForm } from "../forms/create-notification-form";
import { ConfirmSendNotification } from "../modals/confirm-send-notification";
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "../ui/card";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "../ui/sheet";

export function PushNotificationDetails() {
	const params = useParams();
	const navigate = useNavigate();
	const [isAlertOpen, setIsAlertOpen] = useState(false);
	const [pushMode, setPushMode] = useState<"test" | "prod">("test");
	const [isEditing, setIsEditing] = useState(false);
	const { data: notification } = useGetNotification(params.id);
	const { mutate: deleteNotification } = useDeleteNotification();
	const queryClient = useQueryClient();

	const sanitizedContent = DOMPurify.sanitize(notification?.content ?? "", {
		FORBID_ATTR: ["style"],
	});

	return (
		<div className="space-y-2">
			<Breadcrumbs />
			<h1 className="text-lg">Notification Details</h1>
			{notification ? (
				<>
					<Sheet open={isEditing} onOpenChange={setIsEditing}>
						<div className="flex flex-col">
							<div className="flex justify-end">
								<div className="space-x-2">
									<SheetTrigger render={<Button />}>
										Edit Notification
									</SheetTrigger>
									<Button
										variant="destructive"
										onClick={() => {
											deleteNotification(notification.id, {
												onError: (err) => {
													toast(err.message);
												},
												onSuccess: () => {
													toast("Notification created");
													queryClient.invalidateQueries({
														queryKey: ["notifications"],
													});
													navigate("/dashboard/push-notifications");
												},
											});
										}}
									>
										Delete Notification
									</Button>
								</div>
								<SheetContent>
									<SheetHeader>
										<SheetTitle>Edit</SheetTitle>
									</SheetHeader>
									<div className="p-4">
										<CreateNotificationForm
											notification={notification}
											onComplete={() => setIsEditing(false)}
										/>
									</div>
								</SheetContent>
							</div>
						</div>
					</Sheet>
					<Card>
						<CardHeader className="flex justify-between">
							<div>
								<p className="font-bold">Heading: {notification.heading}</p>
								<p className="font-bold">
									Sub Heading: {notification.sub_heading}
								</p>
							</div>
							<p>
								Created: {notification.created} | Updated:{" "}
								{notification.updated}
							</p>
						</CardHeader>
						<CardContent className="space-y-2">
							{/* TODO use actual markdown preview styles from mobile app */}
							<div
								// biome-ignore lint/security/noDangerouslySetInnerHtml: needed for rendering rich text preview
								dangerouslySetInnerHTML={{ __html: sanitizedContent }}
								className="[*a]:text-red max-w-[80vw] overflow-auto"
							/>
						</CardContent>
						<CardFooter className="space-x-2">
							<Button
								variant="default"
								onClick={() => {
									setPushMode("test");
									setIsAlertOpen(true);
								}}
							>
								Send Test
							</Button>
							<Button
								variant="destructive"
								onClick={() => {
									setPushMode("prod");
									setIsAlertOpen(true);
								}}
							>
								Send Production Push
							</Button>
						</CardFooter>
					</Card>
				</>
			) : (
				<p>No notification found</p>
			)}
			<ConfirmSendNotification
				open={isAlertOpen}
				close={() => setIsAlertOpen(false)}
				mode={pushMode}
			/>
		</div>
	);
}
